/**
 * LinkedInScraping.tsx — Scraping / LinkedIn page (Closr style)
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { SectionTitle, LoadingState, ErrorState } from '../DashboardShared'
import { WorkspaceMetric } from './OverviewHome'
import { useAsync, fetchScrapingHealth, fetchLinkedInConnection } from '../api'
import { dayCount } from '../../../lib/plural'
import { Linkedin, CheckCircle2, AlertTriangle } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts'
import { accent, danger, ink, edge, shadow, surface, warning } from '../../../theme/tokens'

export function LinkedInScraping() {
  const { data, loading, error } = useAsync(
    () => Promise.all([fetchScrapingHealth(7), fetchLinkedInConnection()]),
    [],
  )

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const [health, conn] = data ?? [{ days: [], summary: { date: '', ok: 0, blocked: 0, error: 0, total: 0, successRate: 0 } }, { connected: false, expiresAt: null }]
  const s = health.summary
  const expiresIn = conn.expiresAt ? Math.floor((new Date(conn.expiresAt).getTime() - Date.now()) / 86400000) : 0

  return (
    <div className="page-enter flex flex-col gap-5">
      <SectionTitle title="LinkedIn Scraping" subtitle="Session health and scraping success metrics" />

      {/* Connection card */}
      <div className="k-card">
        <div className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-control bg-brand-linkedin flex items-center justify-center flex-shrink-0">
            <Linkedin size={20} className="text-ink-on-accent" />
          </div>
          <div className="flex-1">
            <div className="text-14 font-semibold text-ink-primary">LinkedIn Session</div>
            <div className="text-12 text-ink-secondary mt-1">
              {conn.connected
                ? `Connected — session expires in ${dayCount(expiresIn)}`
                : 'Not connected — please authenticate'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {conn.connected
              ? <span className="flex items-center gap-2 text-12 font-semibold text-success"><CheckCircle2 size={14} /> Active</span>
              : <span className="flex items-center gap-2 text-12 font-semibold text-danger"><AlertTriangle size={14} /> Disconnected</span>
            }
          </div>
        </div>
      </div>

      {/* Metrics -- emoji decoration retired with the closr tiles (the
          P10-1 no-icons precedent; k-metric has no icon slot) */}
      <div className="grid grid-cols-4 gap-3">
        <WorkspaceMetric label="Successful Scrapes (7d)" value={s.ok} />
        <WorkspaceMetric label="Blocked" value={s.blocked} />
        <WorkspaceMetric label="Errors" value={s.error} />
        <WorkspaceMetric label="Success Rate" value={`${(s.successRate * 100).toFixed(1)}%`} />
      </div>

      {/* Daily chart */}
      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Daily Scraping Results</div>
            <div className="k-card-sub">Last 7 days</div>
          </div>
        </div>
        <div className="p-5">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={health.days} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke={edge['row-faint']} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: ink.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: ink.muted }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }} cursor={{ fill: surface['row-hover'] }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="ok" name="OK" fill={accent.soft} stackId="a" />
              <Bar dataKey="blocked" name="Blocked" fill={warning.DEFAULT} stackId="a" />
              <Bar dataKey="error" name="Error" fill={danger.DEFAULT} stackId="a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
